import {Fields, Submenu, CreatedReports} from './_models'

export type ReportFormData = {
  [tabId: number]: {
    [fieldId: string]: {
      label: string
      value: any
    }
  }
}

export interface CalculationOptions {
  previousReports?: CreatedReports[] | any[]
  currentVoyageId?: number
  currentLegId?: number
  currentReportId?: number
  currentStepFields?: Fields[]
  currentTabId?: number
}

export const normalize = (value: string | undefined | null) =>
  String(value ?? '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()

export const roundTo2 = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100

export const parseNumber = (value: any): number | null => {
  if (value === undefined || value === null || value === '') return null
  const parsed = Number(String(value).replace(/,/g, '').trim())
  return Number.isFinite(parsed) ? parsed : null
}

export const asDisplayNumber = (value: number) => roundTo2(value).toFixed(2)

export const isDistanceMadeGoodLabel = (label: string): boolean => {
  const norm = normalize(label)
  if (norm.includes('total') || norm.includes('leg') || norm.includes('cumul')) return false
  return (
    norm.includes('distance made good') ||
    norm.includes('distance observed') ||
    norm.includes('distance travelled') ||
    norm.includes('distance traveled') ||
    norm.includes('distance travel') ||
    norm.includes('dist made good') ||
    norm.includes('dist. made good') ||
    norm.includes('dist observed') ||
    norm.includes('dist travelled') ||
    norm.includes('dist traveled') ||
    norm.includes('observed distance') ||
    norm.includes('logged distance') ||
    norm === 'distance (nm)' ||
    norm === 'distance' ||
    norm === 'dist'
  )
}

export const isTotalDistanceLabel = (label: string): boolean => {
  const norm = normalize(label)
  return (
    norm.includes('total distance') ||
    norm.includes('total dist') ||
    norm.includes('cumulative distance') ||
    norm.includes('cumul distance') ||
    norm.includes('ttidist') ||
    norm.includes('tti dist')
  )
}

export const isSteamingHoursLabel = (label: string): boolean => {
  const norm = normalize(label)
  return (
    norm.includes('steaming hours') ||
    norm.includes('steaming time') ||
    norm.includes('hours at sea') ||
    norm.includes('hours spent at sea') ||
    norm.includes('hours steamed') ||
    norm.includes('steaming hrs') ||
    norm.includes('steaming hr') ||
    norm.includes('engine hours') ||
    norm.includes('me running hours') ||
    norm.includes('sea hours') ||
    norm.includes('time since last report') ||
    norm.includes('time since last rpt') ||
    norm.includes('hours since last report') ||
    norm.includes('time elapsed') ||
    norm.includes('elapsed time') ||
    norm.includes('duration (hours)') ||
    norm.includes('duration (hrs)') ||
    norm.includes('steaming duration') ||
    norm.includes('report duration') ||
    norm.includes('observed time') ||
    norm.includes('time observed') ||
    norm.includes('hours in period') ||
    norm.includes('period hours') ||
    (norm.includes('steaming') && norm.includes('hour')) ||
    norm === 'steaming'
  )
}

export const isAverageSpeedLabel = (label: string): boolean => {
  const norm = normalize(label)
  if (
    norm.includes('general') ||
    norm.includes('gen avg') ||
    norm.includes('gen speed') ||
    norm.includes('total') ||
    norm.includes('leg') ||
    norm.includes('overall') ||
    norm.includes('engine') ||
    norm.includes('through water') ||
    norm.includes('water') ||
    norm.includes('ordered') ||
    norm.includes('slip') ||
    norm.includes('em log')
  ) {
    return false
  }

  return (
    norm.includes('average speed') ||
    norm.includes('avg speed') ||
    norm.includes('speed (24') ||
    norm.includes('speed 24') ||
    norm === 'speed' ||
    norm === 'speed (kts)' ||
    norm === 'speed (knots)'
  )
}

export const isGeneralAverageSpeedLabel = (label: string): boolean => {
  const norm = normalize(label)
  if (norm.includes('leg')) return false
  if (
    norm.includes('24 hour') ||
    norm.includes('24hour') ||
    norm.includes('24 hr') ||
    norm.includes('24hr') ||
    norm.includes('24-hour') ||
    norm.includes('daily') ||
    norm.includes('engine') ||
    norm.includes('water') ||
    norm.includes('ordered')
  ) {
    return false
  }
  return (
    norm.includes('general average speed') ||
    norm.includes('general speed') ||
    norm.includes('total average speed') ||
    norm.includes('overall average speed') ||
    norm.includes('gen average speed') ||
    norm.includes('gen avg speed') ||
    norm.includes('gen speed') ||
    norm.includes('gen avg') ||
    norm.includes('general avg speed') ||
    norm.includes('general avg') ||
    norm.includes('total avg speed') ||
    norm.includes('overall avg speed')
  )
}

export const isLegWiseAverageSpeedLabel = (label: string): boolean => {
  const norm = normalize(label)
  return (
    norm.includes('leg-wise average speed') ||
    norm.includes('leg average speed') ||
    norm.includes('leg wise average speed') ||
    norm.includes('leg avg speed') ||
    norm.includes('leg general average speed') ||
    norm.includes('leg-wise avg speed') ||
    norm.includes('leg wise avg speed') ||
    norm.includes('general average speed (leg)') ||
    norm.includes('general avg speed (leg)') ||
    norm.includes('average speed (leg)')
  )
}

export const extractReportFieldValue = (
  report: any,
  matcher: (label: string) => boolean,
  fieldLabelMap?: Map<number, string>
): number | null => {
  if (!report) return null

  // 1. Check report.values array (backend format)
  if (Array.isArray(report.values)) {
    for (const item of report.values) {
      const fieldId =
        typeof item?.field === 'object' && item?.field !== null
          ? Number(item.field.id)
          : Number(item?.field)

      let label =
        item?.label ??
        (typeof item?.field === 'object' ? item.field.label : undefined)

      if (!label && Number.isFinite(fieldId)) {
        if (fieldLabelMap?.has(fieldId)) {
          label = fieldLabelMap.get(fieldId)
        } else {
          const menus =
            report?.assignment?.customTemplate?.menus ||
            report?.assignment?.template?.menus ||
            []
          for (const m of menus) {
            for (const sm of m?.submenus || []) {
              const matchedF = (sm?.fields || []).find((f: any) => Number(f?.id) === fieldId)
              if (matchedF?.label) {
                label = matchedF.label
                break
              }
            }
            if (label) break
          }
        }
      }

      if (label && matcher(label)) {
        const parsed = parseNumber(item.valueText ?? item.value)
        if (parsed !== null) return parsed
      }
    }
  }

  // 2. Check report.formData format (UI state format)
  if (report.formData && typeof report.formData === 'object') {
    for (const tabKey of Object.keys(report.formData)) {
      const tabData = report.formData[tabKey]
      if (tabData && typeof tabData === 'object') {
        for (const fieldKey of Object.keys(tabData)) {
          const entry = tabData[fieldKey]
          if (entry && entry.label && matcher(entry.label)) {
            const parsed = parseNumber(entry.value)
            if (parsed !== null) return parsed
          }
        }
      }
    }
  }

  return null
}

export const calculatePositionReportMetrics = (
  tabs: Submenu[],
  formData: ReportFormData,
  options: CalculationOptions = {}
) => {
  const {
    previousReports = [],
    currentVoyageId = 0,
    currentLegId = 0,
    currentReportId = 0,
  } = options

  // Find current report values from formData directly first
  let currentDistance: number | null = null
  let currentHours: number | null = null
  let currentAverageSpeed: number | null = null

  if (formData && typeof formData === 'object') {
    Object.keys(formData).forEach((tabKey) => {
      const tabData = formData[Number(tabKey)]
      if (tabData && typeof tabData === 'object') {
        Object.keys(tabData).forEach((fieldKey) => {
          const entry = tabData[fieldKey]
          if (entry && entry.label) {
            const numVal = parseNumber(entry.value)
            if (numVal !== null) {
              if (currentAverageSpeed === null && isAverageSpeedLabel(entry.label)) {
                currentAverageSpeed = numVal
              }
              if (currentDistance === null && isDistanceMadeGoodLabel(entry.label)) {
                currentDistance = numVal
              }
              if (currentHours === null && isSteamingHoursLabel(entry.label)) {
                currentHours = numVal
              }
            }
          }
        })
      }
    })
  }

  // Also check tabs if not found in formData
  if (currentDistance === null || currentHours === null || currentAverageSpeed === null) {
    tabs.forEach((tab) => {
      (tab.fields || []).forEach((field) => {
        if (!field.isActive) return
        const storedVal = formData?.[tab.id]?.[String(field.id)]?.value
        const numVal = parseNumber(storedVal)
        if (numVal === null) return

        const label = field.label
        if (currentAverageSpeed === null && isAverageSpeedLabel(label)) {
          currentAverageSpeed = numVal
        }
        if (currentDistance === null && isDistanceMadeGoodLabel(label)) {
          currentDistance = numVal
        }
        if (currentHours === null && isSteamingHoursLabel(label)) {
          currentHours = numVal
        }
      })
    })
  }

  // Fallback: If average speed is not explicitly entered, derive from distance and hours if available
  if (
    currentAverageSpeed === null &&
    currentDistance !== null &&
    currentHours !== null &&
    currentHours > 0
  ) {
    currentAverageSpeed = roundTo2(currentDistance / currentHours)
  }

  // Build field label lookup from current tabs for resolving ID-only backend report values
  const fieldLabelMap = new Map<number, string>()
  tabs.forEach((tab) => {
    ;(tab.fields || []).forEach((field) => {
      if (field && field.id) {
        fieldLabelMap.set(Number(field.id), field.label)
      }
    })
  })

  // Filter previous submitted reports for Voyage and Leg
  let prevVoyageDistance = 0
  let prevVoyageHours = 0
  const prevVoyageSpeeds: number[] = []

  let prevLegDistance = 0
  let prevLegHours = 0
  const prevLegSpeeds: number[] = []

  if (Array.isArray(previousReports)) {
    previousReports.forEach((r: any) => {
      if (!r) return

      const rId = Number(r.id ?? 0)
      if (currentReportId > 0 && rId === currentReportId) return

      const rStatus = normalize(r.status)
      if (rStatus && rStatus !== 'submitted' && rStatus !== 'approved') return

      const rVoyageId = Number(
        r?.voyage?.id ?? r?.voyageDto?.id ?? r?.voyageId ?? r?.voyage ?? 0
      )
      const rLegId = Number(
        r?.legId ?? r?.legSequenceNumber ?? r?.legDto?.id ?? r?.leg?.id ?? 0
      )

      const d = extractReportFieldValue(r, isDistanceMadeGoodLabel, fieldLabelMap) || 0
      const h = extractReportFieldValue(r, isSteamingHoursLabel, fieldLabelMap) || 0

      let speed = extractReportFieldValue(r, isAverageSpeedLabel, fieldLabelMap)
      if (speed === null && d > 0 && h > 0) {
        speed = roundTo2(d / h)
      }

      // Match Voyage
      const matchesVoyage =
        currentVoyageId > 0
          ? rVoyageId === currentVoyageId
          : rVoyageId === 0 || rVoyageId === currentVoyageId

      if (matchesVoyage) {
        prevVoyageDistance += d
        prevVoyageHours += h
        if (speed !== null && Number.isFinite(speed)) {
          prevVoyageSpeeds.push(speed)
        }

        // Match Leg
        const matchesLeg =
          currentLegId > 0 ? rLegId === currentLegId : true

        if (matchesLeg) {
          prevLegDistance += d
          prevLegHours += h
          if (speed !== null && Number.isFinite(speed)) {
            prevLegSpeeds.push(speed)
          }
        }
      }
    })
  }

  const cDist = currentDistance ?? 0
  const cHrs = currentHours ?? 0

  const totalVoyageDistance = prevVoyageDistance + cDist
  const totalVoyageHours = prevVoyageHours + cHrs

  const totalLegDistance = prevLegDistance + cDist
  const totalLegHours = prevLegHours + cHrs

  // Calculate General Average Speed using Average Speed
  const voyageSpeeds = [...prevVoyageSpeeds]
  if (currentAverageSpeed !== null && Number.isFinite(currentAverageSpeed)) {
    voyageSpeeds.push(currentAverageSpeed)
  }

  const legSpeeds = [...prevLegSpeeds]
  if (currentAverageSpeed !== null && Number.isFinite(currentAverageSpeed)) {
    legSpeeds.push(currentAverageSpeed)
  }

  const generalAverageSpeed =
    voyageSpeeds.length > 0
      ? voyageSpeeds.reduce((sum, s) => sum + s, 0) / voyageSpeeds.length
      : totalVoyageHours > 0
        ? totalVoyageDistance / totalVoyageHours
        : 0

  const legWiseAverageSpeed =
    legSpeeds.length > 0
      ? legSpeeds.reduce((sum, s) => sum + s, 0) / legSpeeds.length
      : totalLegHours > 0
        ? totalLegDistance / totalLegHours
        : 0

  return {
    totalDistanceMadeGood: roundTo2(totalVoyageDistance),
    generalAverageSpeed: roundTo2(generalAverageSpeed),
    legWiseAverageSpeed: roundTo2(legWiseAverageSpeed),
    totalVoyageHours: roundTo2(totalVoyageHours),
    totalLegDistance: roundTo2(totalLegDistance),
    totalLegHours: roundTo2(totalLegHours),
  }
}

export const applyPositionReportCalculations = (
  tabs: Submenu[],
  formData: ReportFormData,
  options: CalculationOptions = {}
): ReportFormData => {
  const {currentStepFields = [], currentTabId} = options

  // Collect all known fields across tabs, active currentStepFields, and formData
  const allFieldsByTab: Record<
    number,
    {fieldId: string | number; label: string}[]
  > = {}

  // 1. Add fields from tabs
  tabs.forEach((tab) => {
    if (!allFieldsByTab[tab.id]) allFieldsByTab[tab.id] = []
    ;(tab.fields || []).forEach((field) => {
      if (field && field.isActive !== false) {
        allFieldsByTab[tab.id].push({fieldId: field.id, label: field.label})
      }
    })
  })

  // 2. Add fields from currentStepFields (active tab fields state)
  if (currentStepFields.length && currentTabId) {
    if (!allFieldsByTab[currentTabId]) allFieldsByTab[currentTabId] = []
    currentStepFields.forEach((field) => {
      if (field && field.isActive !== false) {
        const exists = allFieldsByTab[currentTabId].some(
          (f) => String(f.fieldId) === String(field.id)
        )
        if (!exists) {
          allFieldsByTab[currentTabId].push({fieldId: field.id, label: field.label})
        }
      }
    })
  }

  // 3. Add fields from formData entries
  Object.keys(formData || {}).forEach((tabIdStr) => {
    const tabId = Number(tabIdStr)
    if (!allFieldsByTab[tabId]) allFieldsByTab[tabId] = []
    const tabData = formData[tabId]
    if (tabData && typeof tabData === 'object') {
      Object.keys(tabData).forEach((fieldIdStr) => {
        const entry = tabData[fieldIdStr]
        if (entry && entry.label) {
          const exists = allFieldsByTab[tabId].some(
            (f) => String(f.fieldId) === fieldIdStr
          )
          if (!exists) {
            allFieldsByTab[tabId].push({fieldId: fieldIdStr, label: entry.label})
          }
        }
      })
    }
  })

  if (!Object.keys(allFieldsByTab).length) return formData

  const metrics = calculatePositionReportMetrics(tabs, formData, options)
  let hasChanges = false
  const nextFormData: ReportFormData = {...formData}

  const upsertValue = (tabId: number, fieldId: string | number, label: string, value: string) => {
    const existingValue = nextFormData?.[tabId]?.[String(fieldId)]?.value
    if (String(existingValue ?? '') === value) return

    hasChanges = true
    nextFormData[tabId] = {
      ...(nextFormData[tabId] || {}),
      [String(fieldId)]: {
        label,
        value,
      },
    }
  }

  Object.keys(allFieldsByTab).forEach((tabIdStr) => {
    const tabId = Number(tabIdStr)
    const fieldList = allFieldsByTab[tabId]

    fieldList.forEach(({fieldId, label}) => {
      if (isTotalDistanceLabel(label)) {
        upsertValue(tabId, fieldId, label, asDisplayNumber(metrics.totalDistanceMadeGood))
      } else if (isLegWiseAverageSpeedLabel(label)) {
        upsertValue(tabId, fieldId, label, asDisplayNumber(metrics.legWiseAverageSpeed))
      } else if (isGeneralAverageSpeedLabel(label)) {
        upsertValue(tabId, fieldId, label, asDisplayNumber(metrics.generalAverageSpeed))
      }
    })
  })

  return hasChanges ? nextFormData : formData
}
